/* eslint-disable camelcase */
import _ from 'lodash';
import Service from './abstract';
import { types } from '../constants/files/types';
import statuses from '../constants/users/statuses';
import { ROLE_STRING_TO_ID } from '../constants/permissions/roles';
import personAttributes from '../constants/people/attributes';
import emailTypes from '../constants/emails/types';
import platforms from '../constants/users/platforms';
const BUCKET = process.env.REACT_APP_PUBLIC_S3_BUCKET || 'statictemporalnatura';

const withPersonSmall = 'person,person.attributes,person.attributes.attribute';
const withPersonEmail = 'person.emails,person.emails.type';
const withPersonDocuments = 'person.documents,person.documents.type';
const withPersonTelephones = 'person.telephones,person.telephones.type';
const withPersonAddress = 'person.addresses,person.addresses.state,person.addresses.country,person.addresses.city,person.addresses.type';
const withPersonLevels = 'person.levels,person.levels.type';
const withDevices = 'person.devices,person.devices.type';
const withPerson = `${withPersonSmall},${withDevices},${withPersonTelephones},${withPersonDocuments},${withPersonEmail},${withPersonLevels},`;

const defaultWith = `roles,roles.role,status,${withPerson}`;
const fullWith = `${defaultWith},${withPersonAddress}`;
const fullWidthSmall = `${withPersonSmall},${withPersonDocuments}`;

const defaultOptionsFind = {
    order: 'created_at-DESC',
    with: defaultWith
};
const defaultOptionsGetById = {
    with: fullWith
};

const defaultOptionsFindSmall = {
    order: 'created_at-DESC',
    with: fullWidthSmall
};

const defaultEvents = {
    tiny: false
};

class Users extends Service {
    constructor () {
        super('users');
    }

    get personWithSmall () {
        return withPersonSmall;
    }

    get personWith () {
        return withPerson;
    }

    get fullWith () {
        return fullWith;
    }

    get defaultOptionsFind () {
        return _.merge({}, super.defaultOptionsFind, defaultOptionsFind);
    }

    get defaultOptionsGetById () {
        return _.merge({}, super.defaultOptionsGetById, defaultOptionsGetById);
    }

    findSmall (data = {}) {
        const opts = {
            url: `${this.basePath}/users`,
            qs: _.defaultsDeep({}, data, defaultOptionsFindSmall),
        };

        return this.get(opts).then(users => this.normalizeAll(users));
    }

    findSimple (data = {}) {
        const opts = {
            url: `${this.basePath}/users`,
            qs: _.defaultsDeep({}, data, ''),
        };

        return this.get(opts).then(users => this.normalizeAll(users));
    }

    getAllSegments () {
        return this.get(`${this.basePath}/people/segments`);
    }

    getAllStatuses () {
        return this.get(`${this.basePath}/users/statuses`);
    }

    getDocumentTypes () {
        return this.get({
            url: `${this.basePath}/document/types`,
            qs: { filters: 'id in [1;9;10]' }
        });
    }

    getRoles () {
        return this.get({
            url: `${this.basePath}/permissions/roles`,
            qs: { filters: 'id_platform eq 11' }
        });
    }

    toggle (id, data = {}) {
        const opts = {
            // TODO fix base url. When cross domain resolve for PUT method
            url: `${this.basePath}/users/${id}/toggle`,
            data
        };

        return this.put(opts).then(user => user);
    }

    editById (id, data = {}) {
        const opts = {
            // TODO fix base url. When cross domain resolve for PUT method
            url: `${this.basePath}/users/${id}`,
            data
        };

        return this.put(opts).then(users => this.normalize(users));
    }

    editRole (id, data = {}) {
        const opts = {
            url: `${this.basePath}/users/${id}/roles`,
            data: data.toCreate
        };

        return this.delete({
            url: `${this.basePath}/users/${id}/roles/${data.toDelete}`,
            qs: { idRole: data.toDelete },
            data: { idRole: data.toDelete }
        }).then(res => {
            return this.post(opts);
        });
    }

    blackList (id, data = {}) {
        const opts = {
            url: `${this.basePath}/users/${id}/blacklist`,
            data
        };

        return super.put(opts).then(user => this.normalize(user));
    }

    onboardingOperation (id) {
        const opts = {
            url: `${this.basePath}/users/${id}/vu/operation`
        };

        return super.get(opts);
    }

    userEvents (id, data) {
        const opts = {
            url: `${this.basePath}/users/${id}/events`,
            qs: _.defaultsDeep({}, data, defaultEvents)
        };

        return super.get(opts);
    }

    createManualPayment (id, data) {
        const opts = {
            url: `${this.basePath}/creditcards/${id}/manual/payments`,
            data
        };

        return this.post(opts);
    }

    getFiles (options) {
        return this.get({
            url: `${this.basePath}/files`,
            qs: _.defaultsDeep({}, options, defaultOptionsFind)
        });
    }

    requestUploadFileUrl (options) {
        return this.get({
            url: `${this.basePath}/files/presigned`,
            qs: _.defaultsDeep({}, options)
        });
    }

    uploadFile (url, data) {
        // S3 upload file, request header authorization must be with one space. If you delete the header, it won't work, also if you put it as null
        this.r.defaults.headers.Authorization = ' ';
        return this.post({
            url,
            data,
        });
    }

    async uploadS3 (data) {
        const fileBlob = data.fileUri;

        const params = new URLSearchParams({
            idType: data.idType ? data.idType : types.document.toString(),
            bucket: BUCKET,
            dirname: data.dirname,
            filename: data.filename,
            contentType: 'text/csv'
        });

        const { url, fields, id_file } = await this.get(
            `${this.basePath}/files/presigned?${params.toString()}`
        );
        const idFile = id_file;
        const signature = fields['X-Amz-Signature'];

        await this.put({
            url: `${this.basePath}/files/presigned/${idFile}`,
            data: {
                signature: signature
            }
        });

        const formData = new FormData();

        Object.keys(fields).forEach((key) => {
            formData.append(key, fields[key]);
        });

        formData.append('file', fileBlob);

        await fetch(url, {
            method: 'POST',
            body: formData
        });

        return { url: `${url}/${data.dirname}/${data.filename}`, idFile };
    }

    async uploadS3TaxpagerRegister (data) {
        const fileBlob = data.fileUri;

        const params = new URLSearchParams({
            idType: data.idType ? data.idType : types.document.toString(),
            bucket: BUCKET,
            dirname: data.dirname,
            filename: data.filename,
            idUser: data.idUser,
            contentType: 'text/csv'
        });

        const { url, fields, id_file } = await this.get(
            `${this.basePath}/files/presigned/taxpager/census?${params.toString()}`
        );
        const idFile = id_file;
        const signature = fields['X-Amz-Signature'];

        await this.put({
            url: `${this.basePath}/files/presigned/taxpager/census/${idFile}`,
            data: {
                signature: signature,
                idUser: data.idUser,
                idType: data.idType
            }
        });

        const formData = new FormData();

        Object.keys(fields).forEach((key) => {
            formData.append(key, fields[key]);
        });

        formData.append('file', fileBlob);

        await fetch(url, {
            method: 'POST',
            body: formData
        });

        return { url: `${url}/${data.dirname}/${data.filename}`, idFile };
    }

    validateFiles (data) {
        const opts = {
            url: `${this.basePath}/people/prospect/validate`,
            data
        };

        return this.post(opts);
    }

    processFile (data, qs) {
        const opts = {
            url: `${this.basePath}/people/prospect/process`,
            data,
            qs: _.defaultsDeep({}, qs),

        };

        return this.post(opts);
    }

    updateReport (id, data) {
        return this.put({
            url: `${this.basePath}/files/presigned/${id}`,
            data: {
                signature: data['X-Amz-Signature'],
            }
        });
    }

    getUsersCreditCards (options) {
        return this.get({
            url: `${this.basePath}/creditcards/users/views`,
            qs: _.defaultsDeep({}, options)
        });
    }

    getRemuneratedAccountsList (options = {}) {
        return this.get({
            url: `${this.basePath}/users/accounts/view`,
            qs: _.defaultsDeep({}, options, {
                order: 'created_at-DESC',
                paginationActive: true,
            })
        });
    }

    getUsersList (options) {
        const defaultQs = {
            order: 'id-DESC',
            with: `roles,roles.role,status,attributes,${withPersonSmall},${withPersonEmail},${withPersonDocuments},${withPersonTelephones},${withPersonLevels}`,
            paginationActive: true,
        };

        return this.get({
            url: `${this.basePath}/users`,
            qs: _.defaultsDeep({}, options, defaultQs)
        }).then(response => {
            const result = this.normalizeAll(response);
            const metadata = response.metadata || { total: result.length };

            return {
                result,
                metadata
            };
        });
    }

    getUserCollections (options) {
        return this.get({
            url: `${this.basePath}/creditcards/payments`,
            qs: _.defaultsDeep({}, options)
        });
    }

    getUserWallet (id) {
        return this.get({
            url: `${this.basePath}/wallet/user/card`,
            qs: {
                idUser: id
            }
        });
    }

    getUserWalletHome (id) {
        return this.get({
            url: `${this.basePath}/wallet/user/${id}/home`
        });
    }

    getMovementsCreditCards (options) {
        return this.get({
            url: `${this.basePath}/creditcards/news`,
            qs: _.defaultsDeep({}, options)
        });
    }

    getUserWalletSummary (id) {
        return this.get({
            url: `${this.basePath}/wallet/user/summary`,
            qs: {
                idUser: id
            }
        });
    }

    getSummary (options) {
        return this.get({
            url: `${this.basePath}/creditcards/statements`,
            qs: _.defaultsDeep({}, options)
        });
    }

    getSummaries (options) {
        return this.get({
            url: `${this.basePath}/creditcards/statements/view`,
            qs: _.defaultsDeep({}, options)
        });
    }

    getExpirations (options) {
        return this.get({
            url: `${this.basePath}/creditcards/metrics`,
            qs: _.defaultsDeep({}, options)
        });
    }

    getAccountData (id, options = {}) {
        return this.get({
            url: `${this.basePath}/users/${id}`,
            qs: _.defaultsDeep({}, options, {
                with: 'accounts,accounts.account,accounts.account.wallet'
            })
        });
    }

    normalize (user) {
        if (_.isNil(user)) {
            return user;
        }

        // Prepare default properties
        user = super.normalize(user);

        // Prepare person
        if (user.person) {
            // Prepare attributes from new structure
            if (user.person.attributes && Array.isArray(user.person.attributes)) {
                const attrFirstName = _.find(user.person.attributes, { id_attribute: personAttributes.firstName.id })?.value || '';
                const attrLastName = _.find(user.person.attributes, { id_attribute: personAttributes.lastName.id })?.value || '';
                const formattedName = _.trim(`${attrFirstName} ${attrLastName}`);

                user.formatted_name = formattedName;
                user.formattedName = formattedName;
                user.first_name = attrFirstName;
                user.last_name = attrLastName;

                user.person.first_name = attrFirstName;
                user.person.last_name = attrLastName;
                user.person.formatted_name = formattedName;
                user.person.formattedName = formattedName;

                user.person.rawAttributes = user.person.attributes;
                user.person.attributes = _.keyBy(user.person.attributes, 'description');
            }

            // Prepare attributes from old structure (if applicable)
            if (user.person.attributes && !Array.isArray(user.person.attributes)) {
                user.person.rawAttributes = user.person.attributes;
                user.person.attributes = _.keyBy(user.person.attributes, 'description');

                // Prepare formatted name.
                user.formattedName = '';
                if (user.person.attributes.first_name) {
                    user.formattedName = user.person.attributes.first_name.value;
                }
                if (user.person.attributes.last_name) {
                    user.formattedName = _.trim(`${user.formattedName} ${user.person.attributes.last_name.value}`);
                }
                user.formatted_name = user.formattedName;
            }

            // Prepare documents
            if (user.person.documents && user.person.documents.length) {
                const doc = _.find(user.person.documents, { id_document_type: 1 }) || user.person.documents[0];
                user.document = doc.description;
                user.person.document = { description: doc.description };
            }

            // Prepare emails.
            // A person can share a BO user and a Wallet account, so the same person may have
            // several emails of different types (principal/Wallet, backoffice, etc.). `normalize`
            // is shared across platforms, so we pick the email according to the user's platform:
            //   - Backoffice users: only the active backoffice email (type id 4).
            //   - Any other platform (e.g. Wallet clients): only the active principal email
            //     (type id 1).
            // In both cases, if there is no matching email we show '-' instead of falling back
            // to another type, which would show an incorrect address.
            if (user.person.emails && user.person.emails.length) {
                const idPlatform = user.id_platform || user.idPlatform;
                let email;
                if (idPlatform === platforms.backoffice) {
                    const isBackofficeEmail = e => (e.id_email_type || e.type?.id) === emailTypes.backoffice.id;
                    email = _.find(user.person.emails, e => e.active === 1 && isBackofficeEmail(e));
                }
                else {
                    // Wallet/consumer screens must show the principal email (type id 1), which is
                    // the client's own Wallet address. If there is no active principal we show '-'
                    // instead of another type (e.g. a backoffice one), which would be incorrect.
                    const isPrincipalEmail = e => (e.id_email_type || e.type?.id) === emailTypes.principal.id;
                    email = _.find(user.person.emails, e => e.active === 1 && isPrincipalEmail(e));
                }
                const description = email ? email.description : '-';
                user.email = description;
                user.person.email = { description };
            }
        }

        // Prepare Status String from id_user_status
        const idStatus = user.id_user_status || user.status?.id;
        if (idStatus && !user.user_status) {
            user.user_status = _.findKey(statuses, s => s.id === idStatus);
        }

        // Prepare Role String from roles array
        if (user.roles && Array.isArray(user.roles) && user.roles.length > 0) {
            const firstRole = user.roles[0];
            const idRole = firstRole.id_role || firstRole.role?.id;
            const roleKey = _.findKey(ROLE_STRING_TO_ID, id => id === idRole);
            user.role = roleKey;
        }

        return user;
    }

    getPaymentLinks (options) {
        return this.get({
            url: `${this.basePath}/wallet/links/payment/links`,
            qs: _.defaultsDeep({}, options, { paginationActive: true })
        });
    }

    getPaymentMovements (options) {
        return this.get({
            url: `${this.basePath}/wallet/links/payment-movements`,
            qs: _.defaultsDeep({}, options, { paginationActive: true })
        });
    }

    getPaymentLinksPosition (id, options) {
        return this.get({
            url: `${this.basePath}/paymentlinks/calculate/user/${id}`,
            qs: _.defaultsDeep({}, options)
        });
    }

    getLevels () {
        return this.get(`${this.basePath}/people/levels`);
    }

    getConsultants (options) {
        return this.get({
            url: `${this.basePath}/people/prospect`,
            qs: _.defaultsDeep({}, options, { paginationActive: true })
        });
    }

    validateConsultantByDni (dni) {
        return this.get({
            url: `${this.basePath}/prospect/${dni}`
        });
    }

    getPaymentChargebacks (options) {
        return this.get({
            url: `${this.basePath}/wallet/links/payment-chargeback`,
            qs: _.defaultsDeep({}, options, { paginationActive: true })
        });
    }

    registerChargeback (data) {
        const opts = {
            url: `${this.basePath}/wallet/links/payment-chargeback`,
            data
        };

        return this.post(opts);
    }

    executeCensus (options = {}) {
        const opts = {
            url: `${this.basePath}/afip/census/execute`,
            qs: _.defaultsDeep({}, options)
        };

        return this.get(opts);
    }

    getPaymentSchedule ({ month, year } = {}) {
        const opts = {
            url: `${this.basePath}/payment/schedule`,
            qs: { month, year }
        };

        return this.get(opts);
    }

    updateFraudRule (idRule, { idAction, idStatus }) {
        return this.put({
            url: `${this.basePath}/fraudcontrol/rules/${idRule}`,
            data: { idAction, idStatus }
        });
    }

    createSupplierNetting ({ amount, datePayment } = {}) {
        const opts = {
            url: `${this.basePath}/utilityBills/supplier/netting`,
            data: { amount, datePayment }
        };

        return this.post(opts);
    }
}

export default new Users();
